/* ─────────────────────────────────────────────────────────────────
   UI/UX DESIGN — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx

   Every topic sets linkType: 'figma' — submissions here are shareable
   Figma links, not GitHub repos, so the task page swaps the link
   field's label/validation/placeholder accordingly.
───────────────────────────────────────────────────────────────── */

export const UIUX_MODULES = [
  {
    module: 1,
    title: "Design Fundamentals & Figma Basics",
    emoji: "🎨",
    topics: [
      {
        id: 1,
        topic: "Design Principles & Figma Basics",
        description: "Learn core visual design principles — contrast, hierarchy, alignment, spacing — and get comfortable navigating Figma.",
        motivation: "Great start! Every well-designed product is built on principles like these — you just learned the language of design. 🎨",
        resource: {
          name: "Figma for Beginners — Official Course",
          url: "https://www.figma.com/resources/learn-design/",
        },
        task: "Create a UI style guide in Figma for a mobile fintech app.",
        requirements: [
          "Define a color palette (primary, secondary, neutral, semantic)",
          "Typography scale — at least 5 text styles",
          "Component library: Buttons (3 variants), Inputs, Cards",
          "Spacing system using an 8pt grid",
          "Export as a shareable Figma link",
        ],
        linkType: "figma",
      },
      {
        id: 2,
        topic: "Typography & Color Systems",
        description: "Learn how to pair typefaces and build an accessible, scalable color system.",
        motivation: "Your type and color system is production-ready — this is the foundation every screen you design will build on. 🖋️",
        resource: {
          name: "Figma — Typography Fundamentals",
          url: "https://www.figma.com/resources/learn-design/typography/",
        },
        task: "Expand your fintech style guide with a full typographic and color specification.",
        requirements: [
          "Pair a display typeface with a body typeface",
          "Define light and dark theme color tokens",
          "Check color contrast for accessibility (WCAG AA minimum)",
          "Show typography in context (headings, body, captions, labels)",
          "Share the updated Figma file link",
        ],
        linkType: "figma",
      },
    ],
  },
  {
    module: 2,
    title: "Wireframing & Design Systems",
    emoji: "📐",
    topics: [
      {
        id: 3,
        topic: "Low-Fidelity Wireframes",
        description: "Learn to sketch and structure a user flow before adding any visual polish.",
        motivation: "Structure before beauty — you just made the decision every senior designer makes first. Strong instinct! 📐",
        resource: {
          name: "Figma — Wireframing Basics",
          url: "https://www.figma.com/resources/learn-design/wireframing/",
        },
        task: "Wireframe a complete onboarding flow for the fintech app (signup to first transaction).",
        requirements: [
          "At least 5 low-fidelity screens (grayscale, boxes and text only)",
          "Clear user flow arrows connecting each screen",
          "Annotate key decisions or edge cases directly on the wireframes",
          "Cover: welcome, signup, KYC/verification, home, first transaction",
          "Share the Figma link with wireframes on one page/frame",
        ],
        linkType: "figma",
      },
      {
        id: 4,
        topic: "Design Systems & Component Libraries",
        description: "Learn to build reusable Figma components with variants, so your designs stay consistent as they scale.",
        motivation: "You just built a real design system — the same approach used by product teams at every serious tech company. 🧩",
        resource: {
          name: "Figma — Components & Variants",
          url: "https://help.figma.com/hc/en-us/articles/360055471353-Guide-to-components-in-Figma",
        },
        task: "Turn your style guide into a reusable component library with variants.",
        requirements: [
          "Button component with variants: primary, secondary, disabled, loading",
          "Input component with variants: default, focused, error, disabled",
          "Card and list-item components reused across screens",
          "Use Figma auto-layout so components resize properly",
          "Share the Figma file with the components page clearly labeled",
        ],
        linkType: "figma",
      },
    ],
  },
  {
    module: 3,
    title: "Prototyping",
    emoji: "✨",
    topics: [
      {
        id: 5,
        topic: "High-Fidelity UI Screens",
        description: "Learn to turn wireframes into polished, high-fidelity screens using your design system.",
        motivation: "Your screens look real now! This is the moment a rough idea starts feeling like an actual product. ✨",
        resource: {
          name: "Google UX Design Certificate — Coursera",
          url: "https://www.coursera.org/professional-certificates/google-ux-design",
        },
        task: "Design high-fidelity versions of your onboarding flow using your component library.",
        requirements: [
          "All 5+ screens fully designed using your design system components",
          "Consistent spacing, color, and typography across every screen",
          "At least one empty/error state designed (e.g. failed verification)",
          "Mobile-first layout (390px width)",
          "Share the updated Figma link",
        ],
        linkType: "figma",
      },
      {
        id: 6,
        topic: "Interactive Prototyping & Micro-interactions",
        description: "Learn to link screens together into a clickable prototype with realistic transitions.",
        motivation: "You designed a real user flow. Recruiters notice this level of detail. Incredible work! 🎬",
        resource: {
          name: "Figma — Prototyping Basics",
          url: "https://help.figma.com/hc/en-us/articles/360040314193-Guide-to-prototyping-in-Figma",
        },
        task: "Wire your onboarding screens into a fully clickable Figma prototype.",
        requirements: [
          "Prototype links connecting every screen in the flow",
          "At least one micro-interaction (button press state, toggle animation)",
          "Realistic transition timing and easing between screens",
          "A working back button on every screen",
          "Share the prototype link (not just the design file)",
        ],
        linkType: "figma",
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 7,
        topic: "Complete App Prototype Walkthrough",
        description: "Apply everything learned throughout the MicroIntern to design and present a complete app experience.",
        motivation: "Outstanding! You designed a full product experience from principles to prototype. That's certificate-worthy. 🎉",
        resource: {
          name: "Figma — Learn Design Resources",
          url: "https://www.figma.com/resources/learn-design/",
        },
        task: "Finalize your fintech app prototype and record a short walkthrough explaining your design decisions.",
        requirements: [
          "Complete, clickable prototype covering onboarding through a core task",
          "Design system used consistently across all screens",
          "Light and dark theme versions of at least 2 key screens",
          "A 2–3 minute Loom (or similar) recording walking through your design decisions",
          "Add the Loom link to your Figma file's cover page/description",
          "Share the final Figma prototype link",
        ],
        linkType: "figma",
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const UIUX_TASKS = UIUX_MODULES.flatMap((m) => m.topics);
